import { ClickAwayListener, Grid, Popper, Tooltip, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC, MouseEvent } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';

import useAnchorState from '../hooks/useAnchorState';
import ArrowRight from '../images/arrow-right-dropdown.svg';
import CalendarIcon from '../images/calendar.svg';
import CloseIcon from '../images/close.svg';
import InfoIcon from '../images/info-grey.svg';
import Caption from '../Typography/Caption';
import useStyles from './styles';

export interface DatepickerProps {
  className?: string;
  error?: string;
  locale: CalendarProps['locale'];
  maxDate?: Date;
  minDate?: Date;
  onDateChange: (value: Date | Date[] | undefined) => void;
  /**
   * `fountain-ui-components` is locale agnostic, and it's up to the consumers to provide
   *  a localized value to render, e.g. `<FormattedDate />`
   */
  renderDate: (value: Date) => React.ReactNode;
  /**
   * Prefer using an example, localized date as a placeholder rather than a string like "mm/dd/yyy"
   */
  renderPlaceholder?: () => React.ReactNode;
  /**
   * If we start allowing this component to leverage date ranges, this type should be
   * updated to: `Date | Date[]`
   */
  value?: Date;
  alignError?: 'left' | 'right';
  canOpen?: boolean;
  tooltipMessage?: string;
  allowRemovingDate?: boolean;
}

export const Datepicker: FC<DatepickerProps> = ({
  value,
  onDateChange,
  minDate,
  maxDate,
  className,
  error,
  locale = 'en-US',
  renderDate,
  renderPlaceholder = () => '',
  alignError = 'left',
  canOpen = true,
  tooltipMessage = '',
  allowRemovingDate = false,
  ...popperProps
}) => {
  const classes = useStyles();

  const { anchorEl: containerElement, toggle: toggleSelectingDate, handleClose } = useAnchorState();

  const onChange: CalendarProps['onChange'] = selectedDate => {
    if (onDateChange) {
      onDateChange(selectedDate);
    }
    handleClose();
  };

  const tooltip = () => {
    return (
      <Tooltip title={tooltipMessage}>
        <img alt="Tooltip Info" src={InfoIcon} />
      </Tooltip>
    );
  };
  const handleClearDate = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onDateChange) {
      onDateChange(undefined);
    }
  };

  return (
    <Grid className={classNames(classes.root, className)}>
      <Grid
        alignItems="center"
        className={classNames(classes.dayContainer, {
          active: !!containerElement,
          error: !!error,
          [classes.grayInput]: !canOpen,
        })}
        container
        justify="space-between"
        onClick={canOpen ? toggleSelectingDate : undefined}
      >
        <Typography className={classes.black}>{value ? renderDate(value) : renderPlaceholder()}</Typography>
        {canOpen ? <img alt="Calendar" src={CalendarIcon} /> : tooltip()}
        {allowRemovingDate && value && (
          <button
            aria-label="Clear date"
            className={classes.clearButton}
            onClick={handleClearDate}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            type="button"
          >
            <img alt="Clear" src={CloseIcon} />
          </button>
        )}
      </Grid>
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose} touchEvent="onTouchStart">
        <Popper
          anchorEl={containerElement}
          className={classes.calendarContainer}
          open={!!containerElement && canOpen}
          {...popperProps}
        >
          <Calendar
            calendarType="US"
            className={classNames(classes.calendar, {
              visible: !!containerElement,
            })}
            locale={locale}
            maxDate={maxDate}
            minDate={minDate}
            minDetail="decade"
            next2Label={null} // Date | undefined
            nextLabel={<img alt="right-arrow" src={ArrowRight} />}
            onChange={onChange}
            prev2Label={null}
            prevLabel={<img alt="left-arrow" className={classes.leftArrow} src={ArrowRight} />}
            tileClassName={classes.calendarTile}
            value={value}
          />
        </Popper>
      </ClickAwayListener>
      {error && (
        <Caption align={alignError} classes={{ root: classes.errorMessage }} color="error">
          {error}
        </Caption>
      )}
    </Grid>
  );
};
